// Detalle de una orden de trabajo: datos, tareas, repuestos planificados,
// ejecutores y ACCIONES por estado (programar / iniciar / cerrar / cancelar).
//
// Máquina de estados del backend:
//   abierta → programada → en_proceso → cerrada
//   abierta/programada → en_proceso ; cualquiera no-final → cancelada
// Los 409 estado_invalido (carrera entre dos usuarios) se muestran con el
// mensaje del backend y se refresca el detalle.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { OrdenTrabajoMttoDetail, RepuestoMtto } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Icon } from '../../components/ui/icon';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import {
  useAgregarEjecutorOt,
  useAgregarRepuestoOt,
  useAgregarTareaOt,
  useCancelarOrdenMtto,
  useCerrarOrdenMtto,
  useIniciarOrdenMtto,
  useOrdenMtto,
  useProgramarOrdenMtto,
  useRepuestosMtto,
} from './queries';
import {
  EstadoOtBadge,
  ORIGEN_OT_LABEL,
  esCodigoApi,
  fmtFecha,
  fmtFechaHora,
  fmtNum,
  mensajeError,
  otVencida,
  tipoMantenimientoLabel,
  usePermisosMtto,
} from './shared';

export function OrdenTrabajoDetallePage() {
  const { ordenId } = useParams<{ ordenId: string }>();
  const { data, isLoading, isError, error } = useOrdenMtto(ordenId);

  return (
    <MantenimientoShell
      titulo={data ? `Orden ${data.codigo ?? ''}` : 'Orden de trabajo'}
      subtitulo={
        <Link to="/mantenimiento/ordenes" className="underline underline-offset-2">
          ← Volver a órdenes de trabajo
        </Link>
      }
    >
      {isLoading ? <CargandoModulo mensaje="Cargando orden de trabajo…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}
      {data ? <DetalleOrden orden={data} /> : null}
    </MantenimientoShell>
  );
}

// =============================================================================

function DetalleOrden({ orden }: { orden: OrdenTrabajoMttoDetail }) {
  const { puedeOperar } = usePermisosMtto();
  const [dialogo, setDialogo] = useState<'programar' | 'cerrar' | 'cancelar' | null>(null);
  const iniciar = useIniciarOrdenMtto();

  const esFinal = orden.estado === 'cerrada' || orden.estado === 'cancelada';
  const vencida = otVencida(orden);

  const onIniciar = async () => {
    try {
      await iniciar.mutateAsync(orden.id);
      toast.success('Orden iniciada');
    } catch (err) {
      toast.error(mensajeError(err));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-4 p-5 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <EstadoOtBadge estado={orden.estado} />
              {vencida ? <Badge tone="crit">Vencida</Badge> : null}
              <span className="text-xs text-fg-tertiary">
                {ORIGEN_OT_LABEL[orden.origen]} · {tipoMantenimientoLabel(orden.tipo_mantenimiento)}
              </span>
            </div>
            {puedeOperar && !esFinal ? (
              <div className="flex flex-wrap gap-2">
                {orden.estado === 'abierta' || orden.estado === 'programada' ? (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => setDialogo('programar')}>
                      {orden.estado === 'programada' ? 'Reprogramar' : 'Programar'}
                    </Button>
                    <Button
                      size="sm"
                      disabled={iniciar.isPending}
                      onClick={() => {
                        void onIniciar();
                      }}
                    >
                      Iniciar
                    </Button>
                  </>
                ) : null}
                {orden.estado === 'en_proceso' ? (
                  <Button size="sm" onClick={() => setDialogo('cerrar')}>
                    Cerrar orden
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => setDialogo('cancelar')}>
                  Cancelar orden
                </Button>
              </div>
            ) : null}
          </div>

          <p className="text-sm text-fg">{orden.descripcion}</p>

          <div className="grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-3 lg:grid-cols-6">
            <Dato label="Activo" valor={orden.activo_nombre ?? '—'} />
            <Dato
              label="Fecha programada"
              valor={fmtFecha(orden.fecha_programada)}
              tone={vencida ? 'crit' : undefined}
            />
            <Dato label="Abierta" valor={fmtFechaHora(orden.abierta_at)} />
            <Dato label="Iniciada" valor={fmtFechaHora(orden.iniciada_at)} />
            <Dato label="Cerrada" valor={fmtFechaHora(orden.cerrada_at)} />
            <Dato
              label="Horas reales"
              valor={orden.horas_reales !== null ? `${fmtNum(orden.horas_reales, 2)} h` : '—'}
            />
          </div>

          {orden.estado === 'cerrada' && orden.notas_cierre ? (
            <div className="rounded border border-ok/30 bg-ok-soft px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-ok">
                Notas de cierre
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-sm text-fg">{orden.notas_cierre}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <TareasCard orden={orden} editable={puedeOperar && !esFinal} />
        <RepuestosCard orden={orden} editable={puedeOperar && !esFinal} />
        <EjecutoresCard orden={orden} editable={puedeOperar && !esFinal} />
      </div>

      <ProgramarDialog
        open={dialogo === 'programar'}
        onClose={() => setDialogo(null)}
        orden={orden}
      />
      <CerrarDialog open={dialogo === 'cerrar'} onClose={() => setDialogo(null)} orden={orden} />
      <CancelarDialog
        open={dialogo === 'cancelar'}
        onClose={() => setDialogo(null)}
        orden={orden}
      />
    </div>
  );
}

function Dato({ label, valor, tone }: { label: string; valor: string; tone?: 'crit' }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className={cn('text-sm', tone === 'crit' ? 'font-medium text-crit' : 'text-fg')}>
        {valor}
      </span>
    </div>
  );
}

// =============================================================================
// Tareas
// =============================================================================

function TareasCard({ orden, editable }: { orden: OrdenTrabajoMttoDetail; editable: boolean }) {
  const agregar = useAgregarTareaOt();
  const [descripcion, setDescripcion] = useState('');

  const onAgregar = async () => {
    const desc = descripcion.trim();
    if (!desc) {
      toast.error('Escriba la descripción de la tarea');
      return;
    }
    try {
      await agregar.mutateAsync({
        ordenId: orden.id,
        input: { descripcion: desc, est_min: 0, orden: orden.tareas.length },
      });
      setDescripcion('');
      toast.success('Tarea agregada');
    } catch (err) {
      toast.error(mensajeError(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tareas ({orden.tareas.length})</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {orden.tareas.length === 0 ? (
          <p className="text-sm text-fg-tertiary">Sin tareas registradas.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {orden.tareas.map((t) => (
              <li key={t.id} className="flex items-start gap-2 text-sm">
                <span
                  aria-hidden
                  className={cn(
                    'mt-0.5 flex size-4 flex-shrink-0 items-center justify-center rounded border',
                    t.hecha ? 'border-ok bg-ok-soft text-ok' : 'border-border text-transparent',
                  )}
                >
                  <Icon name="check" size={11} stroke={2} />
                </span>
                <span
                  className={cn('flex-1', t.hecha ? 'text-fg-tertiary line-through' : 'text-fg')}
                >
                  {t.descripcion}
                </span>
                {t.est_min > 0 ? (
                  <span className="text-xs tabular-nums text-fg-tertiary">{t.est_min} min</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {editable ? (
          <div className="mt-1 flex gap-2 border-t border-border pt-3">
            <Input
              placeholder="Nueva tarea…"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void onAgregar();
                }
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={agregar.isPending}
              onClick={() => {
                void onAgregar();
              }}
            >
              Agregar
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Repuestos planificados (se consumen del stock al CERRAR la orden)
// =============================================================================

function RepuestosCard({ orden, editable }: { orden: OrdenTrabajoMttoDetail; editable: boolean }) {
  const agregar = useAgregarRepuestoOt();
  const repuestos = useRepuestosMtto();
  const [repuestoId, setRepuestoId] = useState('');
  const [cantidad, setCantidad] = useState('1');

  const nombrePorId = new Map<string, RepuestoMtto>((repuestos.data ?? []).map((r) => [r.id, r]));

  const onAgregar = async () => {
    if (!repuestoId) {
      toast.error('Seleccione un repuesto');
      return;
    }
    const cant = Number(cantidad);
    if (!Number.isFinite(cant) || cant <= 0) {
      toast.error('La cantidad debe ser mayor a cero');
      return;
    }
    try {
      await agregar.mutateAsync({
        ordenId: orden.id,
        input: { repuesto_id: repuestoId, cantidad: cant },
      });
      setRepuestoId('');
      setCantidad('1');
      toast.success('Repuesto agregado a la orden');
    } catch (err) {
      toast.error(mensajeError(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Repuestos ({orden.repuestos.length})</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {orden.repuestos.length === 0 ? (
          <p className="text-sm text-fg-tertiary">Sin repuestos planificados.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {orden.repuestos.map((r) => {
              const rep = nombrePorId.get(r.repuesto_id);
              return (
                <li key={r.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 text-fg">
                    {rep ? rep.descripcion : 'Repuesto'}
                    {rep?.codigo ? (
                      <span className="ml-1 font-mono text-xs text-fg-tertiary">{rep.codigo}</span>
                    ) : null}
                  </span>
                  <span className="tabular-nums text-fg-secondary">
                    × {fmtNum(r.cantidad, 2)}
                    {rep?.unidad ? ` ${rep.unidad}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {orden.estado !== 'cerrada' && orden.repuestos.length > 0 ? (
          <p className="text-[11px] text-fg-tertiary">El stock se descuenta al cerrar la orden.</p>
        ) : null}
        {editable ? (
          <div className="mt-1 flex gap-2 border-t border-border pt-3">
            <Select value={repuestoId} onChange={(e) => setRepuestoId(e.target.value)}>
              <option value="">Repuesto…</option>
              {(repuestos.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.codigo ? `${r.codigo} · ` : ''}
                  {r.descripcion}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              min="0.01"
              step="any"
              className="w-20"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={agregar.isPending}
              onClick={() => {
                void onAgregar();
              }}
            >
              Agregar
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Ejecutores
// =============================================================================

function EjecutoresCard({ orden, editable }: { orden: OrdenTrabajoMttoDetail; editable: boolean }) {
  const agregar = useAgregarEjecutorOt();
  const [nombre, setNombre] = useState('');
  const [cargo, setCargo] = useState('');

  const onAgregar = async () => {
    const n = nombre.trim();
    if (!n) {
      toast.error('Escriba el nombre del ejecutor');
      return;
    }
    try {
      await agregar.mutateAsync({
        ordenId: orden.id,
        input: { nombre: n, ...(cargo.trim() ? { cargo: cargo.trim() } : {}) },
      });
      setNombre('');
      setCargo('');
      toast.success('Ejecutor agregado');
    } catch (err) {
      toast.error(mensajeError(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ejecutores ({orden.ejecutores.length})</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {orden.ejecutores.length === 0 ? (
          <p className="text-sm text-fg-tertiary">Sin ejecutores asignados.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {orden.ejecutores.map((e) => (
              <li key={e.id} className="flex items-center gap-2 text-sm">
                <Icon
                  name="user"
                  size={14}
                  stroke={1.5}
                  className="flex-shrink-0 text-fg-tertiary"
                />
                <span className="flex-1 text-fg">{e.nombre}</span>
                {e.cargo ? <span className="text-xs text-fg-tertiary">{e.cargo}</span> : null}
              </li>
            ))}
          </ul>
        )}
        {editable ? (
          <div className="mt-1 flex flex-col gap-2 border-t border-border pt-3">
            <div className="flex gap-2">
              <Input
                placeholder="Nombre…"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
              <Input
                placeholder="Cargo (opcional)"
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="self-end"
              disabled={agregar.isPending}
              onClick={() => {
                void onAgregar();
              }}
            >
              Agregar ejecutor
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Dialogs de transición de estado
// =============================================================================

function ProgramarDialog({
  open,
  onClose,
  orden,
}: {
  open: boolean;
  onClose: () => void;
  orden: OrdenTrabajoMttoDetail;
}) {
  const programar = useProgramarOrdenMtto();
  const [fecha, setFecha] = useState(orden.fecha_programada ?? '');

  const onConfirmar = async () => {
    try {
      await programar.mutateAsync({
        id: orden.id,
        input: fecha ? { fecha_programada: fecha } : {},
      });
      toast.success('Orden programada');
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'estado_invalido')) {
        toast.error(mensajeError(err));
        onClose();
        return;
      }
      toast.error(mensajeError(err));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Programar orden</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ot-prog-fecha">Fecha programada</Label>
          <Input
            id="ot-prog-fecha"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
          <p className="text-[11px] text-fg-tertiary">
            Si la deja vacía, la orden queda programada sin fecha específica.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={programar.isPending}
            onClick={() => {
              void onConfirmar();
            }}
          >
            Programar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CerrarDialog({
  open,
  onClose,
  orden,
}: {
  open: boolean;
  onClose: () => void;
  orden: OrdenTrabajoMttoDetail;
}) {
  const cerrar = useCerrarOrdenMtto();
  const [horas, setHoras] = useState('');
  const [notas, setNotas] = useState('');

  const onConfirmar = async () => {
    const horasNum = horas.trim() === '' ? undefined : Number(horas);
    if (horasNum !== undefined && (!Number.isFinite(horasNum) || horasNum < 0)) {
      toast.error('Las horas reales deben ser un número positivo');
      return;
    }
    try {
      await cerrar.mutateAsync({
        id: orden.id,
        input: {
          ...(horasNum !== undefined ? { horas_reales: horasNum } : {}),
          ...(notas.trim() ? { notas_cierre: notas.trim() } : {}),
        },
      });
      toast.success('Orden cerrada');
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'estado_invalido')) {
        // Otra persona la cerró/canceló primero: el detalle ya se refresca.
        toast.error(mensajeError(err));
        onClose();
        return;
      }
      toast.error(mensajeError(err));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cerrar orden</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {orden.repuestos.length > 0 ? (
            <p className="rounded border border-border bg-surface-overlay px-3 py-2 text-xs text-fg-secondary">
              Al cerrar se descuentan del inventario los {orden.repuestos.length} repuesto(s)
              planificados de la orden.
            </p>
          ) : null}
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-cerrar-horas">Horas reales</Label>
            <Input
              id="ot-cerrar-horas"
              type="number"
              min="0"
              step="any"
              placeholder="Ej. 2.5"
              value={horas}
              onChange={(e) => setHoras(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-cerrar-notas">Notas de cierre</Label>
            <Textarea
              id="ot-cerrar-notas"
              rows={3}
              placeholder="Qué se hizo, hallazgos, pendientes…"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={cerrar.isPending}
            onClick={() => {
              void onConfirmar();
            }}
          >
            {cerrar.isPending ? 'Cerrando…' : 'Cerrar orden'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelarDialog({
  open,
  onClose,
  orden,
}: {
  open: boolean;
  onClose: () => void;
  orden: OrdenTrabajoMttoDetail;
}) {
  const cancelar = useCancelarOrdenMtto();
  const [razon, setRazon] = useState('');

  const onConfirmar = async () => {
    if (!razon.trim()) {
      toast.error('Debe indicar la razón para cancelar la orden');
      return;
    }
    try {
      await cancelar.mutateAsync({ id: orden.id, input: { razon: razon.trim() } });
      toast.success('Orden cancelada');
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'estado_invalido')) {
        toast.error(mensajeError(err));
        onClose();
        return;
      }
      toast.error(mensajeError(err));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancelar orden</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ot-cancelar-razon">Razón (obligatoria)</Label>
          <Textarea
            id="ot-cancelar-razon"
            rows={3}
            placeholder="Por qué se cancela esta orden…"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
          />
          <p className="text-[11px] text-fg-tertiary">
            La cancelación queda registrada en la auditoría con esta razón.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Volver
          </Button>
          <Button
            disabled={cancelar.isPending || !razon.trim()}
            onClick={() => {
              void onConfirmar();
            }}
          >
            Cancelar orden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
